import { formatMoney } from '@theme/utilities';
import { CartUpdatedEvent } from '@theme/events';

class KitDecouverte extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.selectedProductId = null;
    this.pendingProductId = null;
    this.includedPrice = 0;
    this.addToCartBehavior = 'open_cart';

    this.onOpenDrawer = this.#handleOpenDrawer.bind(this);
    this.onCloseDrawer = this.#handleCloseDrawer.bind(this);
    this.onBackdropClick = this.#handleBackdropClick.bind(this);
    this.onFilterClick = this.#handleFilterClick.bind(this);
    this.onProductSelect = this.#handleProductSelect.bind(this);
    this.onProductKeyDown = this.#handleProductKeyDown.bind(this);
    this.onConfirm = this.#handleConfirm.bind(this);
    this.onAddToCart = this.#handleAddToCart.bind(this);
    this.onKeyDown = this.#handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.drawer = this.querySelector('[data-ref="kit-drawer"]');
    this.includedPrice = parseInt(this.dataset.includedPrice, 10) || 0;
    this.addToCartBehavior = document.querySelector('cart-icon')?.addToCartBehavior || 'open_cart';
    this.selectedProductId = this.dataset.selectedProductId || null;
    this.pendingProductId = this.selectedProductId;

    if (this.drawer && this.drawer.parentElement !== document.body) {
      const drawerWidth = this.dataset.drawerWidth;
      if (drawerWidth) {
        this.drawer.style.setProperty('--kit-drawer-width', `${drawerWidth}px`);
      }
      document.body.appendChild(this.drawer);
    }

    this.querySelectorAll('[data-ref="kit-open"]').forEach((el) => {
      el.addEventListener('click', this.onOpenDrawer);
    });

    this.drawer?.querySelector('[data-ref="kit-close"]')?.addEventListener('click', this.onCloseDrawer);
    this.drawer?.addEventListener('click', this.onBackdropClick);
    this.drawer?.querySelectorAll('[data-ref="kit-filter"]').forEach((el) => {
      el.addEventListener('click', this.onFilterClick);
    });
    this.drawer?.querySelectorAll('[data-ref="kit-product"]').forEach((el) => {
      el.addEventListener('click', this.onProductSelect);
      el.addEventListener('keydown', this.onProductKeyDown);
    });
    this.drawer?.querySelector('[data-ref="kit-confirm"]')?.addEventListener('click', this.onConfirm);
    this.querySelector('[data-ref="kit-add"]')?.addEventListener('click', this.onAddToCart);
    document.addEventListener('keydown', this.onKeyDown);

    this.#updatePrice();
    this.#syncConfirmState();
  }

  disconnectedCallback() {
    this.querySelectorAll('[data-ref="kit-open"]').forEach((el) => {
      el.removeEventListener('click', this.onOpenDrawer);
    });

    this.drawer?.querySelector('[data-ref="kit-close"]')?.removeEventListener('click', this.onCloseDrawer);
    this.drawer?.removeEventListener('click', this.onBackdropClick);
    this.drawer?.querySelectorAll('[data-ref="kit-filter"]').forEach((el) => {
      el.removeEventListener('click', this.onFilterClick);
    });
    this.drawer?.querySelectorAll('[data-ref="kit-product"]').forEach((el) => {
      el.removeEventListener('click', this.onProductSelect);
      el.removeEventListener('keydown', this.onProductKeyDown);
    });
    this.drawer?.querySelector('[data-ref="kit-confirm"]')?.removeEventListener('click', this.onConfirm);
    this.querySelector('[data-ref="kit-add"]')?.removeEventListener('click', this.onAddToCart);
    document.removeEventListener('keydown', this.onKeyDown);

    if (this.drawer?.parentElement === document.body) {
      this.drawer.remove();
    }
  }

  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
      this.#handleCloseDrawer();
    }
  }

  #handleOpenDrawer(event) {
    event.preventDefault();
    event.stopPropagation();
    this.pendingProductId = this.selectedProductId;
    this.#syncPendingSelection();
    this.drawer?.classList.remove('is-closing');
    this.drawer?.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  #handleCloseDrawer() {
    if (!this.drawer) return;

    this.drawer.classList.add('is-closing');
    setTimeout(() => {
      this.drawer.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125);
  }

  #handleBackdropClick(event) {
    if (event.target !== this.drawer) return;
    this.#handleCloseDrawer();
  }

  #handleFilterClick(event) {
    event.stopPropagation();

    const button = event.currentTarget;
    if (button.classList.contains('is-active')) return;

    const collectionId = button.dataset.collectionId;
    if (!collectionId) return;

    this.drawer.querySelectorAll('[data-ref="kit-filter"]').forEach((el) => {
      const isActive = el === button;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.drawer.querySelectorAll('[data-ref="kit-collection"]').forEach((el) => {
      el.hidden = el.dataset.collectionId !== collectionId;
    });
  }

  #handleProductSelect(event) {
    event.stopPropagation();

    const productEl = event.currentTarget;
    if (productEl.getAttribute('aria-disabled') === 'true') return;

    this.pendingProductId = productEl.dataset.productId;
    this.#syncPendingSelection();
    this.#syncConfirmState();
  }

  #handleProductKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#handleProductSelect(event);
  }

  #handleConfirm(event) {
    event?.stopPropagation();

    if (!this.pendingProductId) return;

    const productEl = this.drawer.querySelector(
      `[data-ref="kit-product"][data-product-id="${this.pendingProductId}"]`,
    );
    if (!productEl) return;

    this.selectedProductId = this.pendingProductId;
    this.dataset.selectedProductId = this.selectedProductId;

    const summaryImage = this.querySelector('[data-ref="kit-selected-image"]');
    const summaryTitle = this.querySelector('[data-ref="kit-selected-title"]');
    const summaryEmpty = this.querySelector('[data-ref="kit-selected-empty"]');
    const summaryFilled = this.querySelector('[data-ref="kit-selected-filled"]');

    const imageSrc = productEl.dataset.image;
    const title = productEl.dataset.title;
    const price = parseInt(productEl.dataset.price, 10) || 0;
    const variantId = productEl.dataset.variantId;

    if (summaryImage) {
      if (imageSrc) {
        summaryImage.src = imageSrc;
        summaryImage.alt = title || '';
        summaryImage.classList.remove('placeholder-image');
      } else {
        summaryImage.src = summaryImage.dataset.placeholderSrc || '';
        summaryImage.alt = '';
        summaryImage.classList.add('placeholder-image');
      }
      summaryImage.hidden = false;
    }

    if (summaryTitle) {
      summaryTitle.textContent = title || '';
    }

    if (summaryEmpty) summaryEmpty.hidden = true;
    if (summaryFilled) summaryFilled.hidden = false;

    const selectedRow = this.querySelector('[data-ref="kit-selected-row"]');
    if (selectedRow) {
      selectedRow.dataset.price = String(price);
      selectedRow.dataset.variantId = variantId || '';
    }

    const openButton = this.querySelector('[data-ref="kit-open"]');
    if (openButton && this.dataset.modifyLabel) {
      openButton.textContent = this.dataset.modifyLabel;
    }

    this.#updatePrice();
    this.#syncAddButtonState();
    this.#handleCloseDrawer();
  }

  async #handleAddToCart(event) {
    event.preventDefault();

    const button = this.querySelector('[data-ref="kit-add"]');
    const includedVariantId = this.dataset.includedVariantId;
    const selectedRow = this.querySelector('[data-ref="kit-selected-row"]');
    const selectedVariantId = selectedRow?.dataset.variantId;

    if (!includedVariantId || !selectedVariantId) return;

    button?.classList.add('is-loading');
    button?.setAttribute('disabled', 'disabled');

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) {
      sections.push(cartDrawer.dataset.sectionId);
    }

    const kitTitle = this.dataset.kitTitle || '';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(includedVariantId),
              quantity: 1,
              properties: kitTitle ? { _kit: kitTitle } : undefined,
            },
            {
              id: Number(selectedVariantId),
              quantity: 1,
              properties: kitTitle ? { _kit: kitTitle } : undefined,
            },
          ],
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      if (data.status && data.status >= 400) {
        throw new Error(data.description || data.message || 'Error');
      }

      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
      const newItemCount = currentItemCount != null ? currentItemCount + 2 : undefined;

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'kit-decouverte',
            sections: data.sections,
          },
        ),
      );

      if (this.addToCartBehavior === 'notification') {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: {
              type: 'success',
              message: Theme.translations?.addToCartNotificationMessage || '',
            },
          }),
        );
      }
    } catch (error) {
      console.error(error);
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: { type: 'error', message: String(error?.message || error) },
        }),
      );
    } finally {
      button?.classList.remove('is-loading');
      this.#syncAddButtonState();
    }
  }

  #syncPendingSelection() {
    this.drawer?.querySelectorAll('[data-ref="kit-product"]').forEach((el) => {
      const isSelected = el.dataset.productId === this.pendingProductId;
      el.classList.toggle('is-selected', isSelected);
      el.setAttribute('aria-checked', isSelected ? 'true' : 'false');

      const checkbox = el.querySelector('[data-ref="kit-product-checkbox"]');
      if (checkbox) {
        checkbox.checked = isSelected;
      }
    });
  }

  #syncConfirmState() {
    const confirmButton = this.drawer?.querySelector('[data-ref="kit-confirm"]');
    if (!confirmButton) return;

    if (this.pendingProductId) {
      confirmButton.removeAttribute('disabled');
    } else {
      confirmButton.setAttribute('disabled', 'disabled');
    }
  }

  #syncAddButtonState() {
    const button = this.querySelector('[data-ref="kit-add"]');
    const selectedRow = this.querySelector('[data-ref="kit-selected-row"]');
    const hasSelection = Boolean(selectedRow?.dataset.variantId);

    if (!button) return;

    if (hasSelection) {
      button.removeAttribute('disabled');
    } else {
      button.setAttribute('disabled', 'disabled');
    }
  }

  #updatePrice() {
    const selectedRow = this.querySelector('[data-ref="kit-selected-row"]');
    const selectedPrice = parseInt(selectedRow?.dataset.price, 10) || 0;
    const total = this.includedPrice + selectedPrice;
    const priceEl = this.querySelector('[data-ref="kit-total-price"]');
    const buttonPriceEl = this.querySelector('[data-ref="kit-button-price"]');

    if (priceEl) {
      priceEl.innerHTML = formatMoney(total);
    }

    if (buttonPriceEl) {
      buttonPriceEl.innerHTML = formatMoney(total);
    }
  }
}

if (!customElements.get('kit-decouverte')) {
  customElements.define('kit-decouverte', KitDecouverte);
}
